import {useCallback, useEffect, useState} from 'react';
import './edit-activities-group-dialog.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {useForm} from 'react-hook-form';
import {Alert, Grid, Skeleton, SkeletonProps, Stack} from '@mui/material';
import {useAuth} from '../../../contexts/auth/context';
import {DynamicForm, DynamicFormFieldShape} from '../../dynamic-form';
import {Modal} from '../../modal/modal';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {DEFAULT_FORM_VALIDATIONS} from '../../../utils/constants';
import {convertArrayToIndex} from '../../../utils/functions/convert-array-to-index';
import {LoadingConfigurationsType} from '../../../enums';
import {ConfigurationsIndex} from '../../../types';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';

const SKELETON_DEFAULT_PROPS = {
  animation: 'wave',
  variant: 'rounded',
} as SkeletonProps;
interface EditActivitiesGroupDialogProps {
  open: boolean;
  onClose: () => void;
  activitiesSectorId: string;
  activitiesGroupId: string;
  onUpdate: (
    updatedConfigurations: Array<CarePlanActivitiesGroupApiRequestConfigurationsItem>
  ) => Promise<void>;
  defaultValues: any;
}

export const EditActivitiesGroupDialog = ({
  open,
  onClose,
  activitiesSectorId,
  activitiesGroupId,
  onUpdate,
  defaultValues,
}: EditActivitiesGroupDialogProps) => {
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const [activitiesGroup, setActivitiesGroup] = useState<ActivitiesGroup | null | undefined>(
    undefined
  );

  const {t} = useTranslation();

  const authContext = useAuth();

  const formInstance = useForm<DynamicFormFieldShape>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const handleModalClose = () => {
    // close modal
    onClose();

    // reset form
    formInstance.reset();
  };

  const handleUpdateActivity = (
    formData: CarePlanActivitiesGroupApiRequestConfigurationsItem[]
  ) => {
    onUpdate(formData);

    handleModalClose();
  };

  const loadActivityGroupConfigurations = useCallback(() => {
    // Unset previous
    setActivitiesGroup(undefined);

    setConfigurationsIndex(undefined);

    // Start Loading Configurations
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Obtain data
    Promise.all<[Promise<ActivitiesGroup | null>, Promise<Array<CarePlanConfiguration>>]>([
      // Load Activities Group (to obtain the Configuration layout)
      EntityActivitiesRepository.instance.getGroup(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activitiesGroupId
      ),

      // Load Activities Group Configurations
      EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activitiesGroupId
      ),
    ])
      .then(
        ([activitiesGroup, careplanConfiguration]: [
          ActivitiesGroup | null,
          Array<CarePlanConfiguration>,
        ]) => {
          if (activitiesGroup) {
            // Build Configurations Index
            const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
              careplanConfiguration,
              'id'
            );

            // Set
            setActivitiesGroup(activitiesGroup);

            setConfigurationsIndex(configurationsIndex);

            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.log(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [
    activitiesGroupId,
    activitiesSectorId,
    authContext.state.session?.selectedAffiliation?.entity.id,
  ]);

  useEffect(() => {
    if (open) loadActivityGroupConfigurations();
  }, [activitiesSectorId, activitiesGroupId, loadActivityGroupConfigurations, open]);

  // Build
  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      title={`${t(translations.activities_group_entry.modals.edit.title)} ${
        activitiesGroup?.name ? `- ${activitiesGroup?.name}` : ''
      }`}
      aria-labelledby="add-activities-group-dialog-title"
      aria-describedby="add-activities-group-dialog-description"
      maxWidth="sm"
      PaperProps={{sx: {maxWidth: '680px'}}}
      fullWidth
      preventCloseClick
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: handleModalClose,
        },
        {
          label: t(translations.modal.save),
          type: 'nondestructive',
          disabled: !formInstance.formState.isValid,
          form: 'edit-activity-group',
          buttonType: 'submit',
        },
      ]}
    >
      {loadingConfigurations === LoadingConfigurationsType.Loading && (
        <Grid container spacing={4}>
          {new Array(6).fill(1).map((_item: number, index: number) => (
            <Grid item xs={6} key={`skeleton_edit_sub_activity_${index}`}>
              <Stack gap={2} display="flex" flexDirection="column">
                <Skeleton {...SKELETON_DEFAULT_PROPS} width={120} height={20} />
                <Skeleton {...SKELETON_DEFAULT_PROPS} width="100%" height={42} />
              </Stack>
            </Grid>
          ))}
        </Grid>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Error && (
        <Alert severity="error">{t(translations.dynamic_form.error_loading)}</Alert>
      )}

      {loadingConfigurations === LoadingConfigurationsType.Loaded && activitiesGroup && (
        <DynamicForm
          id="edit-activity-group"
          formInstance={formInstance}
          configurationsIndex={configurationsIndex}
          defaultValuesIndex={defaultValues}
          layout={activitiesGroup.carePlanConfigurationLayout}
          activitiesSectorId={activitiesSectorId}
          onSubmit={handleUpdateActivity}
        />
      )}
    </Modal>
  );
};
